import React, { useState, useRef, useEffect } from 'react';
import InlineColorPicker from './InlineColorPicker';
import './ColorField.css';

/**
 * ColorField - A field that combines color picker with text description
 * Users can either pick colors visually or describe them in text
 */
const ColorField = ({ 
  id, 
  display, 
  placeholder = 'e.g. Warm earth tones, Navy blue accents', 
  value, 
  onChange,
  placement = 'down' // 'down' | 'up'
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [textValue, setTextValue] = useState('');
  const containerRef = useRef(null);

  const isEditingRef = useRef(false);
  
  useEffect(() => {
    if (isEditingRef.current) return;
    
    if (value && Array.isArray(value)) {
      const hexPattern = /^#[A-Fa-f0-9]{6}$/;
      // Extract hex colors from array (must match exact hex pattern)
      const colors = value.filter(v => {
        const str = String(v).trim();
        return hexPattern.test(str);
      }).map(v => String(v).trim());
      
      const textParts = value.filter(v => {
        const str = String(v).trim();
        return !hexPattern.test(str) && str.length > 0;
      }).map(v => String(v));
      
      setSelectedColors(colors);
      setTextValue(textParts.length > 0 ? textParts.join(', ') : '');
    } else {
      setSelectedColors([]);
      setTextValue('');
    }
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Combine colors and text into final array value
  const updateValue = (colors, text) => {
    const arrayValue = [];
    if (colors.length > 0) {
      arrayValue.push(...colors);
    }
    if (text !== null && text !== undefined && text.length > 0) {
      const textParts = text.split(',').map(t => t.trim()).filter(t => t.length > 0);
      if (textParts.length > 0) {
        arrayValue.push(...textParts);
      }
    }
    onChange({ target: { name: id, value: arrayValue } });
  };

  const handleColorSelect = (hexColor) => {
    // Add color if not already selected (max 5 colors)
    if (!selectedColors.includes(hexColor) && selectedColors.length < 5) {
      const newColors = [...selectedColors, hexColor];
      setSelectedColors(newColors);
      updateValue(newColors, textValue);
    }
  };

  const handleRemoveColor = (colorToRemove) => {
    const newColors = selectedColors.filter(c => c !== colorToRemove);
    setSelectedColors(newColors);
    updateValue(newColors, textValue);
  };

  const handleTextChange = (e) => {
    const newText = e.target.value;
    setTextValue(newText);
    isEditingRef.current = true;
    updateValue(selectedColors, newText);
    setTimeout(() => {
      isEditingRef.current = false;
    }, 100);
  };

  return (
    <div className="color-field-container" ref={containerRef}>
      {/* Selected Colors Display */}
      {selectedColors.length > 0 && (
        <div className="selected-colors">
          {selectedColors.map((color, index) => (
            <div 
              key={`${color}-${index}`} 
              className="color-chip"
              style={{ backgroundColor: color }}
              onClick={() => handleRemoveColor(color)}
              title="Click to remove"
            >
              <span className="color-chip-remove">×</span>
            </div>
          ))}
        </div>
      )}

      {/* Input Row */}
      <div className="color-input-row">
        {/* Color Picker Toggle */}
        <button 
          type="button"
          className={`color-picker-toggle ${showPicker ? 'active' : ''}`}
          onClick={() => setShowPicker(!showPicker)}
          title="Pick a color"
        >
          <span className="color-picker-icon">🎨</span>
        </button>

        {/* Text Input */}
        <input
          type="text"
          id={id}
          name={id}
          value={textValue}
          onChange={handleTextChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault(); // Prevent form submission
            }
          }}
          placeholder={placeholder}
          className="color-text-input"
        />
      </div>

      {/* Color Picker Dropdown */}
      {showPicker && (
        <div
          className="color-picker-dropdown"
          style={
            placement === 'up'
              ? { top: 'auto', bottom: 'calc(100% + 8px)' }
              : { top: 'calc(100% + 8px)', bottom: 'auto' }
          }
        >
          <InlineColorPicker 
            onColorChange={handleColorSelect}
            onClose={() => setShowPicker(false)}
          />
        </div>
      )}
    </div>
  );
};

export default ColorField;

